import { ComboboxInput, ComboboxList, ComboboxOption, ComboboxPopover } from '@reach/combobox';
import '@reach/combobox/styles.css';
import { PlayPrefix } from 'app/ui-components/typography/PlayPrefix.styles';
import { PlayTextStyles } from 'app/ui-components/typography/PlayText/PlayText.styles';
import styled, { keyframes } from 'styled-components';
import { device, deviceGte } from '../../../ui-components/breakpoints';
import {
    accentGreen,
    darkCoolGrey,
    extraLightCoolGrey,
    fontWeightRegular,
    iconSearch,
    playGrey15,
    playGrey20,
    playGrey5,
    playTransitionRecommendations,
    regularBlack,
    regularWhite,
} from '../../../ui-components/design-tokens';
import { ButtonLink } from '../../../ui-components/PlayButton/PlayButton.styles';
import PlayImage from '../../../ui-components/PlayImage/PlayImage';
import { playFontIcon, playFontSize, regularOutline } from '../../../ui-components/typography/helpers';
import PlayText from '../../../ui-components/typography/PlayText/PlayText';
import { placeholderColor, VisuallyHidden } from '../../../ui-components/utils.styles';

const fadeIn = keyframes`
    0% {
        opacity: 0;
    }

    100% {
        opacity: 1;
    }
`;

export const ShowAllListItem = styled.li`
    padding: 10px;
    border: 2px solid transparent;
`;

export const ShowSearchResultButton = styled(ButtonLink)`
    ${(props) => props.isSelected && `border-color: ${accentGreen}`};
`;

ShowSearchResultButton.displayName = 'ShowSearchResultButton';

export const SearchSuggestionLink = styled.a`
    display: flex;
    ${PlayTextStyles}
    color: ${playGrey15};
    text-decoration: none;
    line-height: 1.2;
    box-sizing: border-box;
    float: left;
    width: 100%;
    padding: 6px 9px;
    ${(props) => props.isGenre && 'padding-left: 9px;'};

    &:focus {
        text-decoration: none;
    }

    &:hover {
        text-decoration: none;
    }

    &:hover .linkText {
        text-decoration: underline;
    }

    &:focus,
    &:hover,
    &:active,
    &:visited {
        color: ${playGrey15};
    }
`;

export const SearchSuggestionThumbnail = styled.figure`
    position: relative;
    width: 37%;
    margin-right: 10px;
    float: left;
    line-height: 0;
    flex-shrink: 0;

    @media ${device.GteSmall} {
        width: 30%;
    }
`;

export const ThumbNailImage = styled(PlayImage)`
    background: ${darkCoolGrey};
`;

export const SearchSuggestionTextWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-self: flex-start;
`;

export const SearchSuggestionLinkText = styled(PlayText)`
    display: block;
    text-align: left;
    line-height: 1.2;
    color: ${regularBlack};
`;

export const SearchSuggestionLinkByline = styled(PlayPrefix)`
    display: block;
    margin-bottom: 3px;
    text-align: left;
`;

export const SearchSuggestionEpisodeText = styled.span`
    font-weight: ${fontWeightRegular};
    ${playFontSize(1.5)};
    color: ${regularBlack};
    display: inline-block;
    text-align: left;
    margin-top: 2px;
`;

export const SearchSuggestionList = styled(ComboboxList)`
    list-style: none;
    margin: 0;
    padding: 0;
`;

export const SearchSuggestionListItem = styled(ComboboxOption)`
    margin-bottom: 5px;
    overflow: hidden;

    &:first-child {
        margin-top: 5px;
    }

    &:last-child {
        margin-bottom: 0;
    }

    &:hover,
    &[data-reach-combobox-option][data-highlighted],
    &:focus {
        ${regularOutline('-2', regularBlack)}
    }

    &[data-reach-combobox-option][data-highlighted][aria-selected='true'] {
        background: none;

        &:hover {
            background: none;
        }
    }

    &[data-reach-combobox-option] {
        background: none;
        &:hover {
            background: none;
        }
    }
`;

export const Heading = styled.h2`
    ${playFontSize(1.5)};
    font-weight: ${fontWeightRegular};
    color: ${regularBlack};
    text-align: left;
    padding: 20px 0 5px;
    margin: 0 14px;
`;

SearchSuggestionListItem.displayName = 'SearchSuggestionListItem';

export const Form = styled.form`
    width: 100%;
    float: left;
    position: relative;
`;

Form.displayName = 'Form';

export const SearchInput = styled(ComboboxInput)`
    border-radius: 19px;
    ${playFontSize(1.6)}
    ${placeholderColor(playGrey20)}

    box-sizing: border-box;
    float: right;
    margin: 0;
    width: 96%;
    line-height: 30px;
    height: 35px;
    background-color: ${regularWhite};
    padding: 0 32px 0 12px;
    position: relative;
    border: 2px solid transparent;
    transition: min-width 300ms ${playTransitionRecommendations};

    @media ${deviceGte(660)} {
        min-width: 200px;
        width: 100%;

        &:focus {
            min-width: 270px;
        }
    }

    @media ${device.GteSmall} {
        height: 33px;
    }

    &[type='search'] {
        ${playFontSize(1.6)};
    }

    /* stylelint-disable selector-no-vendor-prefix */
    &::-ms-clear {
        display: none;
    }

    &::-webkit-input-placeholder {
        ${playFontSize(1.6)}
        line-height: 30px;
    }

    &::-moz-placeholder {
        /* Firefox 19+ */
        ${playFontSize(1.6)}
    }

    &:-ms-input-placeholder {
        ${playFontSize(1.6)}
    }
    /* stylelint-enable selector-no-vendor-prefix */

    &:focus {
        @media ${device.GteMedium} {
            min-width: 310px;
        }

        /* Using a darker "focus" color here for higher contrast */
        border-color: ${accentGreen};

        /* Hack to prevent android and iOS devices from zooming. Gray instead of black to make the text feel smaller. */
        ${playFontSize(1.6)}
        color: ${playGrey5};
        outline: none;
    }
`;

export const LiveUpdates = styled.div`
    ${VisuallyHidden}
`;

export const SubmitButton = styled.button`
    ${playFontSize(2.2)}
    color: ${darkCoolGrey};
    background-color: transparent;
    border: none;
    position: absolute;
    right: 0;
    padding: 5px 12px 0 3px;
    height: 0;

    &:focus {
        ${regularOutline(2)}
    }
    @media ${device.GteSmall} {
        padding: 0 12px 0 3px;
        height: 100%;
    }
`;

export const SearchIcon = styled.span`
    &:before {
        ${playFontIcon(1.8, iconSearch)}
        vertical-align: baseline;
    }
`;

export const SearchLabel = styled.span`
    ${VisuallyHidden}
`;

export const SuggestionsPopOver = styled(ComboboxPopover)`
    &[data-reach-combobox-popover] {
        background: ${extraLightCoolGrey};
        width: 100%;
        clear: both;
        max-height: 1000px;
        overflow: visible;
        float: left;
        margin-top: 8px;
        animation: ${fadeIn} 0.3s ease;
        animation-iteration-count: 1;

        &:before {
            content: '';
            height: 0;
            width: 0;
            position: absolute;
            border-left: 7px solid transparent;
            border-right: 7px solid transparent;
            border-bottom: 7px solid ${extraLightCoolGrey};
            margin-left: -7px;
            margin-top: -7px;
            left: 50%;
        }

        @media ${device.GteSmall} {
            position: absolute;
            top: 43px;
            right: 0;
            width: 350px;
            overflow: visible;

            max-height: 1000px;
            margin-top: 0;

            &:before {
                left: 60%;
            }
        }

        @media ${device.GteLarge} {
            margin-right: 5px;
            width: 380px;
        }
        z-index: 2;
    }
`;
